import { useState } from "react";

import { useDeleteGoogleFileSearchDocument } from "@/queries/google-file-search-store";
import { displayToast, displayErrorToast } from "@/utils/toast";
import { ToastType } from "@/enums";

interface UseDocumentManagementProps {
  selectedStoreName: string | null;
  provider?: string;
}

export const useDocumentManagement = ({
  selectedStoreName,
  provider = "google",
}: UseDocumentManagementProps) => {
  const { mutate: deleteDocument } = useDeleteGoogleFileSearchDocument(provider);
  const [deletingDocNames, setDeletingDocNames] = useState<Set<string>>(new Set());

  const handleDeleteDocument = (params: {
    documentName: string;
    fileName?: string;
    fileId?: string;
    filename?: string;
  }) => {
    if (!selectedStoreName) {
      return;
    }

    setDeletingDocNames(prev => new Set(prev).add(params.documentName));

    deleteDocument(
      {
        storeName: selectedStoreName,
        documentName: params.documentName,
        fileName: params.fileName,
        fileId: params.fileId,
        filename: params.filename,
      },
      {
        onSuccess: () => {
          displayToast("Document deleted successfully", ToastType.success);
          setDeletingDocNames(prev => {
            const next = new Set(prev);
            next.delete(params.documentName);
            return next;
          });
        },
        onError: (error: unknown) => {
          const message = error instanceof Error ? error.message : "Failed to delete document";
          displayErrorToast(message);
          setDeletingDocNames(prev => {
            const next = new Set(prev);
            next.delete(params.documentName);
            return next;
          });
        },
      },
    );
  };

  const isDeletingDocByName = (docName: string) => deletingDocNames.has(docName);

  return {
    handleDeleteDocument,
    isDeletingDocByName,
  };
};
